/**
 * Google-primary search merge:
 * 1) Google Places text results are the catalog
 * 2) Disgo restaurants enrich rows matched by googlePlaceId
 * 3) Specials-text Disgo hits (not already in Google list) are appended
 * 4) Disgo-enriched rows rank first; distance within each group
 */

const priceToLevel = (price) => {
  if (!price) return null;
  if (price === "$") return 1;
  if (price === "$$") return 2;
  if (price === "$$$") return 3;
  return 4;
};

const toDisgoSearchRow = (restaurant, googleMatch, currentLocation, calculateDistance) => {
  const googlePlaceId = restaurant.googlePlaceId || googleMatch?.id || null;
  return {
    ...restaurant,
    id: restaurant._id,
    name: googleMatch?.name || restaurant.name,
    address: googleMatch?.address || "",
    latitude: googleMatch?.latitude ?? restaurant.latitude,
    longitude: googleMatch?.longitude ?? restaurant.longitude,
    rating: googleMatch?.rating ?? restaurant.rating,
    userRatingCount:
      googleMatch?.userRatingCount ?? restaurant.userRatingCount ?? 0,
    priceLevel:
      googleMatch?.priceLevel ?? priceToLevel(restaurant.price),
    types: googleMatch?.types || restaurant.googleTypes || ["restaurant"],
    googlePlaceId,
    isDisgoRestaurant: true,
    isPartner: restaurant.isPartner || false,
    cuisine: restaurant.cuisine,
    matchedViaSpecials: Boolean(restaurant._matchedViaSpecials),
    distance:
      currentLocation &&
      (googleMatch?.latitude ?? restaurant.latitude) != null &&
      (googleMatch?.longitude ?? restaurant.longitude) != null
        ? calculateDistance(
            currentLocation.latitude,
            currentLocation.longitude,
            googleMatch?.latitude ?? restaurant.latitude,
            googleMatch?.longitude ?? restaurant.longitude
          )
        : googleMatch?.distance ?? null,
  };
};

/**
 * @param {object} params
 * @param {Array} params.googlePlacesResults
 * @param {Array} params.disgoRestaurantsByPlaceId - Disgo docs for Google result place ids
 * @param {Array} params.specialsRestaurants - Disgo docs matched via daily_specials text
 * @param {object|null} params.currentLocation
 * @param {Function} params.calculateDistance
 */
export function combineGooglePrimarySearchResults({
  googlePlacesResults = [],
  disgoRestaurantsByPlaceId = [],
  specialsRestaurants = [],
  currentLocation = null,
  calculateDistance,
}) {
  const disgoByPlaceId = new Map();
  for (const restaurant of disgoRestaurantsByPlaceId || []) {
    if (restaurant?.googlePlaceId) {
      disgoByPlaceId.set(restaurant.googlePlaceId, restaurant);
    }
  }

  const googleIds = new Set(
    (googlePlacesResults || []).map((place) => place.id).filter(Boolean)
  );

  const enrichedGoogle = (googlePlacesResults || []).map((place) => {
    const disgo = disgoByPlaceId.get(place.id);
    if (!disgo) {
      return {
        ...place,
        googlePlaceId: place.id,
        isDisgoRestaurant: false,
        distance:
          place.distance ??
          (currentLocation && place.latitude != null && place.longitude != null
            ? calculateDistance(
                currentLocation.latitude,
                currentLocation.longitude,
                place.latitude,
                place.longitude
              )
            : null),
      };
    }

    return toDisgoSearchRow(disgo, place, currentLocation, calculateDistance);
  });

  // Specials discovery: Disgo venues whose specials matched but aren't already in Google hits
  const specialsOnly = [];
  for (const restaurant of specialsRestaurants || []) {
    const placeId = restaurant.googlePlaceId;
    if (placeId && googleIds.has(placeId)) {
      continue;
    }
    if (placeId && disgoByPlaceId.has(placeId)) {
      continue;
    }
    specialsOnly.push(
      toDisgoSearchRow(
        { ...restaurant, _matchedViaSpecials: true },
        null,
        currentLocation,
        calculateDistance
      )
    );
  }

  const byDistance = (a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity);
  const disgoRows = [
    ...enrichedGoogle.filter((row) => row.isDisgoRestaurant),
    ...specialsOnly,
  ].sort(byDistance);
  const googleOnlyRows = enrichedGoogle
    .filter((row) => !row.isDisgoRestaurant)
    .sort(byDistance);

  return [...disgoRows, ...googleOnlyRows];
}
